import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ShareIcon from '@mui/icons-material/Share';
import {
    AppBar,
    Box,
    Card,
    CardContent,
    Chip,
    Collapse,
    IconButton,
    Tooltip as MuiTooltip,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import dinero from 'dinero.js';
import { parse as parseCsv } from 'json2csv';
import moment from 'moment';
import { useDialog } from 'muibox';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import useAxios from '../../../../hooks/useAxios';
import { OnlyFansMetricsWithSubscriber } from '../../../../hooks/useSextforceMetricsCampaignsOverview';
import { MetricCrossSubscriberEarningsResult } from '../../../../hooks/useSextforceMetricsCrossReferenceMetricEarnings';
import { OnlyFansMetricsGroupWithSummary } from '../../../../hooks/useSextforceMetricsGroups';
import useSextforceMetricsGroupsCountersLastOfEveryMonth from '../../../../hooks/useSextforceMetricsGroupsCountersLastOfEveryMonth';
import useSextforceMetricsGroupsSubscribedAtVsTotalSalesDetails from '../../../../hooks/useSextforceMetricsGroupsSubscribedAtVsTotalSalesDetails';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import { SettingsContext } from '../../../../store/SettingsContext';
import { d2f } from '../../../../utils/common';
import NumberChip from '../../../common/NumberChip';
import SextforceMetricsGroupsTableRowMetricRow from './SextforceMetricsGroupsTableRowMetricRow';
import SextforceMetricsSubscribedAtVsSalesSummary from './SextforceMetricsSubscribedAtVsSalesSummary';

type Props = {
    subscriber: OnlyFansSubscriber;
    group: OnlyFansMetricsGroupWithSummary;
    amountType: 'gross' | 'net';
    openGroupDialog: (groupId: string) => void;
    openShareDialog: (groupId: string) => void;
    handleDeleteGroup: (groupId: string) => void;
    timezone: string;
    crossPollinationEarningsMap: Map<string, MetricCrossSubscriberEarningsResult | null>;
    crossPollinationLoadingMap: Map<string, boolean>;
    crossPollinationEarningsSubscriberId: string;
};

// Per-metric cross-pollination earnings of one monthly cohort
type MonthlyCrossEarnings = Map<string, { total: number; totalNet: number }>;

const SextforceMetricsGroupsTableRow = React.memo(
    ({
        subscriber,
        group,
        amountType,
        openGroupDialog,
        openShareDialog,
        handleDeleteGroup,
        timezone,
        crossPollinationEarningsMap,
        crossPollinationLoadingMap,
        crossPollinationEarningsSubscriberId,
    }: Props) => {
        const settingsContext = useContext(SettingsContext);

        const theme: Theme = useTheme();
        const axios = useAxios();
        const dialog = useDialog();

        const [open, setOpen] = useState(false);

        const { data: groupSubscriberdAtVsTotalSalesDetails, isLoading: groupSubscriberdAtVsTotalSalesDetailsLoading } =
            useSextforceMetricsGroupsSubscribedAtVsTotalSalesDetails(subscriber, open ? group._id : undefined, timezone);

        const { data: groupCountersLastOfEveryMonth, isLoading: groupCountersLastOfEveryMonthLoading } =
            useSextforceMetricsGroupsCountersLastOfEveryMonth(subscriber?._id, open ? group._id : undefined);

        const [tabValue, setTabValue] = useState<string | 'total'>('total');

        const handleTabChange = (event: React.SyntheticEvent, newValue: string | 'total') => {
            setTabValue(newValue);
        };

        // Add this helper function at the top of your component, outside the main component function
        const getMonthsFromData = (data: any[]) => {
            if (!data || data.length === 0) return [];

            const monthsSet = new Set<string>();
            data.forEach(point => {
                if (point._id && point._id.interval) {
                    const monthKey = moment(point._id.interval).format('YYYY-MM');
                    monthsSet.add(monthKey);
                }
            });

            return Array.from(monthsSet)
                .sort()
                .map(monthKey => ({
                    key: monthKey,
                    label: moment(monthKey + '-01').format('MMM YYYY'),
                }));
        };

        // Simplified helper functions - let's start from scratch with the data processing
        const getMonthlyDataForSubscriber = (subscriberData: any[] | undefined, monthKey: string, amountType: 'gross' | 'net') => {
            if (!subscriberData || subscriberData.length === 0) {
                return { subscribers: 0, revenue: 0 };
            }

            // Find all data points for this specific month
            const monthData = subscriberData.filter(point => {
                if (!point._id || !point._id.interval) return false;
                const pointMonth = moment(point._id.interval).format('YYYY-MM');
                return pointMonth === monthKey;
            });

            // Sum up all subscribers and revenue for this month
            let totalSubscribers = 0;
            let totalRevenue = 0;

            monthData.forEach(point => {
                // Count subscribers
                totalSubscribers += point.count || 0;

                // Calculate revenue
                let revenue = 0;

                // Try different revenue fields based on your settings
                if (amountType === 'net') {
                    // For net, try net fields first, then calculate from gross
                    revenue =
                        point.totalNet || point.totalNetWithoutSubscriptions || (point.total || point.totalWithoutSubscriptions || 0) * 0.8;
                } else {
                    // For gross, use gross fields
                    revenue = point.total || point.totalWithoutSubscriptions || 0;
                }

                // Convert to number if it's a string/object
                if (typeof revenue !== 'number') {
                    revenue = d2f(revenue);
                }

                totalRevenue += revenue;
            });

            return {
                subscribers: totalSubscribers,
                revenue: totalRevenue,
            };
        };

        const getCumulativeDataForSubscriber = (subscriberData: any[] | undefined, upToMonthKey: string, amountType: 'gross' | 'net') => {
            if (!subscriberData || subscriberData.length === 0) {
                return { subscribers: 0, revenue: 0 };
            }

            // Get all data points up to and including the specified month
            const cumulativeData = subscriberData.filter(point => {
                if (!point._id || !point._id.interval) return false;
                const pointMonth = moment(point._id.interval).format('YYYY-MM');
                return pointMonth <= upToMonthKey;
            });

            // Sum up all subscribers and revenue
            let totalSubscribers = 0;
            let totalRevenue = 0;

            cumulativeData.forEach(point => {
                totalSubscribers += point.count || 0;

                let revenue = 0;
                if (amountType === 'net') {
                    revenue =
                        point.totalNet || point.totalNetWithoutSubscriptions || (point.total || point.totalWithoutSubscriptions || 0) * 0.8;
                } else {
                    revenue = point.total || point.totalWithoutSubscriptions || 0;
                }

                if (typeof revenue !== 'number') {
                    revenue = d2f(revenue);
                }

                totalRevenue += revenue;
            });

            return {
                subscribers: totalSubscribers,
                revenue: totalRevenue,
            };
        };

        // Add this helper function to get previous month key
        const getPreviousMonthKey = (monthKey: string) => {
            const currentMonth = moment(monthKey + '-01');
            const previousMonth = currentMonth.clone().subtract(1, 'month');
            return previousMonth.format('YYYY-MM');
        };

        // Add this state and effect to fetch all subscriber data dynamically
        // This should be added in your main SextforceMetricsGroupsTableRow component

        const [subscriberDataMap, setSubscriberDataMap] = useState<{ [key: string]: any }>({});
        const [loadingSubscriberData, setLoadingSubscriberData] = useState(false);
        const [fetchProgress, setFetchProgress] = useState({ completed: 0, total: 0 });

        const progressPercentage = fetchProgress.total > 0 ? (fetchProgress.completed / fetchProgress.total) * 100 : 0;

        // Create a stable identifier for the current metrics
        const currentMetricIds = useMemo(() => {
            return group.metrics
                .map(m => m._id)
                .sort()
                .join(',');
        }, [group.metrics]);

        // Create a stable base URL
        const baseUrl = useMemo(() => {
            return settingsContext.routes.metrics.base;
        }, [settingsContext.routes.metrics.base]);

        // Effect to fetch all subscriber data when the component opens or metrics change
        useEffect(() => {
            if (!open || !group.metrics || group.metrics.length === 0) {
                return;
            }

            let isMounted = true; // Prevent state updates if component unmounts

            const fetchAllSubscriberData = async () => {
                if (!isMounted) return;

                setLoadingSubscriberData(true);
                const newDataMap: { [key: string]: any } = {};

                // Fetch data for each metric with progress tracking
                let completedCount = 0;

                // Fetch data for each metric
                const fetchPromises = group.metrics.map(async metric => {
                    try {
                        const query = `${baseUrl}${metric.subscriber._id}/${
                            metric.type === 'promoCampaign' ? 'promocampaigns' : 'trials'
                        }/${metric._id}/subscribedat?timezone=${timezone}`;

                        const response = await axios.get(query);
                        if (isMounted) {
                            newDataMap[metric._id!] = { data: response.data, isLoading: false };
                            completedCount++;
                            setFetchProgress({ completed: completedCount, total: group.metrics.length });
                        }
                    } catch (error) {
                        console.error(`Error fetching data for metric ${metric._id}:`, error);
                        if (isMounted) {
                            newDataMap[metric._id!] = { data: null, isLoading: false };
                            completedCount++;
                            setFetchProgress({ completed: completedCount, total: group.metrics.length });
                        }
                    }
                });

                await Promise.all(fetchPromises);

                if (isMounted) {
                    setSubscriberDataMap(newDataMap);
                    setLoadingSubscriberData(false);
                }
            };

            fetchAllSubscriberData();

            // Cleanup function to prevent state updates after unmount
            return () => {
                isMounted = false;
            };
        }, [open, currentMetricIds, baseUrl, timezone]); // Removed axios dependency

        // Deduplicated cross-pollination earnings per monthly cohort, keyed by
        // `${crossAccountSelection}-${monthKey}`. Every fan is attributed to their first-touch
        // link within the group, then only fans whose winning claim falls inside the month
        // participate -- so the column reconciles with the group-level cross-earnings and
        // never double-counts across rows. A `null` entry marks a failed fetch so the
        // fetch loop moves on instead of retrying forever.
        const [monthlyCrossEarnings, setMonthlyCrossEarnings] = useState<{ [key: string]: MonthlyCrossEarnings | null }>({});
        const [monthlyCrossEarningsLoadingKey, setMonthlyCrossEarningsLoadingKey] = useState<string | null>(null);
        const [monthlyCrossEarningsError, setMonthlyCrossEarningsError] = useState<string | null>(null);
        // The all-months loop only runs after the user clicks Calculate and confirms
        const [monthlyCrossEarningsTriggered, setMonthlyCrossEarningsTriggered] = useState(false);

        // Changing the cross account selection invalidates the trigger so the new
        // selection is not calculated until the user asks for it again
        useEffect(() => {
            setMonthlyCrossEarningsTriggered(false);
        }, [crossPollinationEarningsSubscriberId]);

        const monthlyCrossEarningsKey = (monthKey: string) => `${crossPollinationEarningsSubscriberId}-${monthKey}`;

        const crossPollinationAccountSelected =
            crossPollinationEarningsSubscriberId !== 'none' && crossPollinationEarningsSubscriberId !== '';

        const fetchMonthlyCrossEarnings = async (monthKey: string) => {
            if (
                crossPollinationEarningsSubscriberId === 'none' ||
                crossPollinationEarningsSubscriberId === '' ||
                monthlyCrossEarningsLoadingKey !== null ||
                group.metrics.length === 0
            ) {
                return;
            }

            const key = monthlyCrossEarningsKey(monthKey);

            setMonthlyCrossEarningsLoadingKey(key);
            setMonthlyCrossEarningsError(null);

            try {
                // Month boundaries in the dashboard timezone, matching the monthly buckets
                const monthStart = moment.tz(`${monthKey}-01`, timezone).startOf('month');
                const monthEnd = monthStart.clone().add(1, 'month');

                const query = `${settingsContext.routes.metrics.base}${subscriber._id}/crossEarnings/deduped/pg`;

                const response = await axios.post(query, {
                    metricIds: group.metrics.map(metric => metric._id),
                    crossSubscriberIds:
                        crossPollinationEarningsSubscriberId === 'all' ? 'all' : [crossPollinationEarningsSubscriberId],
                    claimsFrom: monthStart.toISOString(),
                    claimsTo: monthEnd.toISOString(),
                });

                const data = response.data as {
                    rows: { metricId: string; total: object; totalNet: object }[];
                };

                // Sum each metric's rows across the selected cross accounts
                const earningsByMetric: MonthlyCrossEarnings = new Map();

                group.metrics.forEach(metric => {
                    earningsByMetric.set(metric._id!, { total: 0, totalNet: 0 });
                });

                data.rows.forEach(row => {
                    const earnings = earningsByMetric.get(row.metricId);

                    if (earnings) {
                        earnings.total += d2f(row.total);
                        earnings.totalNet += d2f(row.totalNet);
                    }
                });

                setMonthlyCrossEarnings(prev => ({ ...prev, [key]: earningsByMetric }));
            } catch (error: any) {
                console.error(`Error fetching monthly cross-pollination earnings for group ${group._id}:`, error);
                setMonthlyCrossEarningsError(
                    error.response?.data?.message || error.message || 'Failed to fetch cross-pollination earnings',
                );

                // Mark the month as attempted so the auto-fetch loop does not retry it forever
                setMonthlyCrossEarnings(prev => ({ ...prev, [key]: null }));
            } finally {
                setMonthlyCrossEarningsLoadingKey(null);
            }
        };

        // Confirms with the user, then kicks off the month-by-month calculation loop.
        // Previous results of the current selection are dropped first, so the button
        // doubles as a recalculate.
        const handleCalculateMonthlyCrossEarnings = async () => {
            try {
                await dialog.confirm({
                    title: 'Calculate Monthly Cross-Pollination Earnings',
                    message:
                        "This calculation analyses each month's new subscribers and all of their transactions, one month at a time, so it can take several minutes to complete depending on the amount of data. The results will appear month by month as they are ready. Please be patient and keep this page open while it runs.",
                    ok: {
                        color: 'primary',
                        text: 'Calculate',
                        variant: 'contained',
                    },
                    cancel: {
                        text: 'Cancel',
                        color: 'secondary',
                        variant: 'text',
                    },
                });
            } catch {
                return;
            }

            setMonthlyCrossEarnings(prev => {
                const next = { ...prev };

                availableMonths.forEach(month => {
                    delete next[monthlyCrossEarningsKey(month.key)];
                });

                return next;
            });
            setMonthlyCrossEarningsError(null);
            setMonthlyCrossEarningsTriggered(true);
        };

        // Simplified component that receives all data as props
        const MonthlySubscriberTable = ({
            metrics,
            selectedMonth,
            amountType,
            timezone,
            theme,
            subscriberDataMap,
        }: {
            metrics: OnlyFansMetricsWithSubscriber[];
            selectedMonth: string;
            amountType: 'gross' | 'net';
            timezone: string;
            theme: any;
            subscriberDataMap: { [key: string]: any };
            isLoading: boolean;
        }) => {
            // Calculate monthly data for each subscriber
            const monthlyData = useMemo(() => {
                return metrics.map(metric => {
                    const subscriberData = subscriberDataMap[metric._id!];
                    const monthlyInfo = getMonthlyDataForSubscriber(subscriberData?.data, selectedMonth, amountType);
                    const previousMonthKey = getPreviousMonthKey(selectedMonth);
                    const previousMonthCumulative = getCumulativeDataForSubscriber(subscriberData?.data, previousMonthKey, amountType);
                    const currentMonthCumulative = getCumulativeDataForSubscriber(subscriberData?.data, selectedMonth, amountType);

                    return {
                        metric,
                        monthlyInfo,
                        previousMonthCumulative,
                        currentMonthCumulative,
                    };
                });
            }, [metrics, subscriberDataMap, selectedMonth, amountType]);

            // Calculate totals from the provided data
            const totals = monthlyData.reduce(
                (acc, item) => {
                    return {
                        newSubscribers: acc.newSubscribers + (item.monthlyInfo ? item.monthlyInfo.subscribers : 0),
                        netRevenue: acc.netRevenue + (item.monthlyInfo ? item.monthlyInfo.revenue : 0),
                        finalNetRevenue: acc.finalNetRevenue + item.currentMonthCumulative.revenue,
                    };
                },
                { newSubscribers: 0, netRevenue: 0, finalNetRevenue: 0 },
            );

            // Format currency values for display
            const formatCurrency = (value: number) => {
                return dinero({
                    amount: value ? Math.trunc(value * 100) : 0,
                    currency: 'USD',
                }).toFormat();
            };

            // Cross-pollination earnings of this month's cohort, if calculated (null = failed)
            const monthCrossEarnings: MonthlyCrossEarnings | null | undefined =
                monthlyCrossEarnings[monthlyCrossEarningsKey(selectedMonth)];
            const monthCrossEarningsLoading = monthlyCrossEarningsLoadingKey === monthlyCrossEarningsKey(selectedMonth);

            const crossEarningsTotal = monthCrossEarnings
                ? Array.from(monthCrossEarnings.values()).reduce(
                      (sum, earnings) => sum + (amountType === 'gross' ? earnings.total : earnings.totalNet),
                      0,
                  )
                : 0;

            return (
                <TableBody>
                    {monthlyData.map(item => {
                        // Check if this year-month is present in countersLastOfEveryMonth
                        const counter = groupCountersLastOfEveryMonth?.metrics[item.metric._id!]?.find(
                            dp => dp.yearMonth === selectedMonth,
                        );

                        return (
                            <TableRow key={item.metric._id} hover>
                                <TableCell
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'black',
                                    }}
                                >
                                    {item.metric.subscriber?.username || 'N/A'}
                                </TableCell>
                                <TableCell align="right">{item.previousMonthCumulative.subscribers.toLocaleString()}</TableCell>
                                <TableCell align="right">
                                    {item.monthlyInfo ? item.monthlyInfo.subscribers.toLocaleString() : '0'}
                                </TableCell>
                                <TableCell align="right">{item.currentMonthCumulative.subscribers.toLocaleString()}</TableCell>
                                <TableCell align="right">
                                    {groupSubscriberdAtVsTotalSalesDetailsLoading ? (
                                        <Skeleton width="100%" />
                                    ) : (
                                        counter?.newTransitionsCount?.toLocaleString() || '0'
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {groupSubscriberdAtVsTotalSalesDetailsLoading ? (
                                        <Skeleton width="100%" />
                                    ) : (
                                        counter?.initialTransitionsCount?.toLocaleString() || '0'
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {groupSubscriberdAtVsTotalSalesDetailsLoading ? (
                                        <Skeleton width="100%" />
                                    ) : (
                                        counter?.newClaimsCount?.toLocaleString() || '0'
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {groupSubscriberdAtVsTotalSalesDetailsLoading ? (
                                        <Skeleton width="100%" />
                                    ) : (
                                        counter?.initialClaimsCount?.toLocaleString() || '0'
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {groupSubscriberdAtVsTotalSalesDetailsLoading ? (
                                        <Skeleton width="100%" />
                                    ) : (
                                        counter?.newSubscribersCount?.toLocaleString() || '0'
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {groupSubscriberdAtVsTotalSalesDetailsLoading ? (
                                        <Skeleton width="100%" />
                                    ) : (
                                        counter?.initialSubscribersCount?.toLocaleString() || '0'
                                    )}
                                </TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {formatCurrency(item.previousMonthCumulative.revenue)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {item.monthlyInfo ? formatCurrency(item.monthlyInfo.revenue) : '$0.00'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {formatCurrency(item.currentMonthCumulative.revenue)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {monthCrossEarningsLoading ? (
                                        <Skeleton width="100%" />
                                    ) : monthCrossEarnings ? (
                                        formatCurrency(
                                            (() => {
                                                const earnings = monthCrossEarnings.get(item.metric._id!);

                                                return earnings ? (amountType === 'gross' ? earnings.total : earnings.totalNet) : 0;
                                            })(),
                                        )
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            -
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {totals.newSubscribers.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            -
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {groupCountersLastOfEveryMonthLoading ? (
                                <Skeleton width="100%" />
                            ) : (
                                Math.max(
                                    0,
                                    (groupCountersLastOfEveryMonth?.summary.find(dp => dp.yearMonth === selectedMonth)?.countTransitions ||
                                        0) -
                                        (groupCountersLastOfEveryMonth?.summary.find(
                                            dp => dp.yearMonth === getPreviousMonthKey(selectedMonth),
                                        )?.countTransitions || 0),
                                ).toLocaleString()
                            )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {groupCountersLastOfEveryMonthLoading ? (
                                <Skeleton width="100%" />
                            ) : (
                                Math.max(
                                    0,
                                    (groupCountersLastOfEveryMonth?.summary.find(dp => dp.yearMonth === selectedMonth)?.claimsCount || 0) -
                                        (groupCountersLastOfEveryMonth?.summary.find(
                                            dp => dp.yearMonth === getPreviousMonthKey(selectedMonth),
                                        )?.claimsCount || 0),
                                ).toLocaleString()
                            )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {groupCountersLastOfEveryMonthLoading ? (
                                <Skeleton width="100%" />
                            ) : (
                                Math.max(
                                    0,
                                    (groupCountersLastOfEveryMonth?.summary.find(dp => dp.yearMonth === selectedMonth)?.countSubscribers ||
                                        groupCountersLastOfEveryMonth?.summary.find(dp => dp.yearMonth === selectedMonth)
                                            ?.subscribeCounts ||
                                        0) -
                                        (groupCountersLastOfEveryMonth?.summary.find(
                                            dp => dp.yearMonth === getPreviousMonthKey(selectedMonth),
                                        )?.subscribeCounts ||
                                            groupCountersLastOfEveryMonth?.summary.find(
                                                dp => dp.yearMonth === getPreviousMonthKey(selectedMonth),
                                            )?.countSubscribers ||
                                            0),
                                ).toLocaleString()
                            )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                            -
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                            {formatCurrency(totals.netRevenue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                            {formatCurrency(totals.finalNetRevenue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                            {monthCrossEarningsLoading ? (
                                <Skeleton width="100%" />
                            ) : monthCrossEarnings ? (
                                formatCurrency(crossEarningsTotal)
                            ) : (
                                '-'
                            )}
                        </TableCell>
                    </TableRow>
                </TableBody>
            );
        };

        // Replace the existing AppBar section with this:
        const availableMonths = groupSubscriberdAtVsTotalSalesDetails ? getMonthsFromData(groupSubscriberdAtVsTotalSalesDetails) : [];

        const availableMonthKeys = availableMonths.map(month => month.key).join(',');

        // Once the user has confirmed the Calculate button, work through the
        // cross-earnings of every month, one month at a time. Each completed month
        // updates the cache, which re-runs this effect to pick up the next
        // uncalculated month -- so the requests stay strictly sequential.
        useEffect(() => {
            if (
                !open ||
                !monthlyCrossEarningsTriggered ||
                crossPollinationEarningsSubscriberId === 'none' ||
                crossPollinationEarningsSubscriberId === '' ||
                loadingSubscriberData ||
                groupSubscriberdAtVsTotalSalesDetailsLoading ||
                monthlyCrossEarningsLoadingKey !== null ||
                group.metrics.length === 0 ||
                availableMonths.length === 0
            ) {
                return;
            }

            const nextMonth = availableMonths.find(month => !(monthlyCrossEarningsKey(month.key) in monthlyCrossEarnings));

            if (nextMonth) {
                fetchMonthlyCrossEarnings(nextMonth.key);
            }
        }, [
            open,
            monthlyCrossEarningsTriggered,
            crossPollinationEarningsSubscriberId,
            loadingSubscriberData,
            groupSubscriberdAtVsTotalSalesDetailsLoading,
            monthlyCrossEarningsLoadingKey,
            availableMonthKeys,
            monthlyCrossEarnings,
        ]);

        // Per-month cross-earnings sums for the Total tab's Monthly Summary table:
        // 'YYYY-MM' -> amount in the displayed gross/net, null = that month failed, key
        // absent = not calculated yet
        const crossPollinationEarningsByMonth: { [monthKey: string]: number | null } | undefined =
            crossPollinationEarningsSubscriberId !== 'none' && crossPollinationEarningsSubscriberId !== ''
                ? availableMonths.reduce((acc: { [monthKey: string]: number | null }, month) => {
                      const earningsByMetric = monthlyCrossEarnings[monthlyCrossEarningsKey(month.key)];

                      if (earningsByMetric === null) {
                          acc[month.key] = null;
                      } else if (earningsByMetric !== undefined) {
                          let sum = 0;

                          earningsByMetric.forEach(earnings => {
                              sum += amountType === 'gross' ? earnings.total : earnings.totalNet;
                          });

                          acc[month.key] = sum;
                      }

                      return acc;
                  }, {})
                : undefined;

        const monthlyCrossEarningsFetching =
            monthlyCrossEarningsLoadingKey !== null ||
            (monthlyCrossEarningsTriggered &&
                crossPollinationEarningsSubscriberId !== 'none' &&
                crossPollinationEarningsSubscriberId !== '' &&
                availableMonths.length > 0 &&
                availableMonths.some(month => !(monthlyCrossEarningsKey(month.key) in monthlyCrossEarnings)));

        // Add this export logic at the parent level (after the availableMonths calculation)
        const createExportData = (selectedMonth: string) => {
            // Calculate monthly data for each subscriber
            const monthlyData = group.metrics.map(metric => {
                const subscriberData = subscriberDataMap[metric._id!];
                const monthlyInfo = getMonthlyDataForSubscriber(subscriberData?.data, selectedMonth, amountType);
                const previousMonthKey = getPreviousMonthKey(selectedMonth);
                const previousMonthCumulative = getCumulativeDataForSubscriber(subscriberData?.data, previousMonthKey, amountType);
                const currentMonthCumulative = getCumulativeDataForSubscriber(subscriberData?.data, selectedMonth, amountType);

                return {
                    metric,
                    monthlyInfo,
                    previousMonthCumulative,
                    currentMonthCumulative,
                };
            });

            // Calculate totals
            const totals = monthlyData.reduce(
                (acc, item) => {
                    return {
                        newSubscribers: acc.newSubscribers + (item.monthlyInfo ? item.monthlyInfo.subscribers : 0),
                        netRevenue: acc.netRevenue + (item.monthlyInfo ? item.monthlyInfo.revenue : 0),
                        finalNetRevenue: acc.finalNetRevenue + item.currentMonthCumulative.revenue,
                    };
                },
                { newSubscribers: 0, netRevenue: 0, finalNetRevenue: 0 },
            );

            return { monthlyData, totals };
        };

        // Format currency values for display
        const formatCurrency = (value: number) => {
            return dinero({
                amount: value ? Math.trunc(value * 100) : 0,
                currency: 'USD',
            }).toFormat();
        };

        // Export functions
        const exportMonthlyData = (format: 'csv' | 'excel', selectedMonth: string) => {
            const { monthlyData, totals } = createExportData(selectedMonth);

            // Cross-pollination earnings of this month's cohort, if calculated (null = failed)
            const monthCrossEarnings: MonthlyCrossEarnings | null | undefined =
                monthlyCrossEarnings[monthlyCrossEarningsKey(selectedMonth)];

            const headers = [
                { label: 'Name of the model', value: 'modelName' },
                { label: 'Initial Subscribers (Found)', value: 'initialSubscribers' },
                { label: 'New Subscribers (Found)', value: 'newSubscribers' },
                { label: 'Total Subscribers (Found)', value: 'totalSubscribers' },
                { label: 'New Clicks (OF Counters)', value: 'newTransitionsCount' },
                { label: 'Clicks at Month End (OF Counters)', value: 'initialTransitionsCount' },
                { label: 'New Claims (OF Counters)', value: 'newClaimsCount' },
                { label: 'Claims at Month End (OF Counters)', value: 'initialClaimsCount' },
                { label: 'New Subs (OF Counters)', value: 'newSubscribersCount' },
                { label: 'Subs at Month End (OF Counters)', value: 'initialSubscribersCount' },
                { label: 'Initial Net Revenue', value: 'initialNetRevenue' },
                { label: 'Net Revenue', value: 'netRevenue' },
                { label: 'Final Net Revenue', value: 'finalNetRevenue' },
                {
                    label: `Cross-Pollination Earnings ${amountType === 'gross' ? 'Gross' : 'Net'}`,
                    value: 'crossPollinationEarnings',
                },
            ];

            // Prepare data for export
            const exportData: Array<{
                modelName: string;
                initialSubscribers: number | string;
                newSubscribers: number;
                totalSubscribers: number | string;
                initialNetRevenue: string;
                netRevenue: string;
                finalNetRevenue: string;
                crossPollinationEarnings: string;
            }> = monthlyData.map(item => {
                const counters = groupCountersLastOfEveryMonth?.metrics[item.metric._id!]?.find(dp => dp.yearMonth === selectedMonth);
                const crossEarnings = monthCrossEarnings?.get(item.metric._id!);

                return {
                    modelName: item.metric.subscriber?.username || 'N/A',
                    initialSubscribers: item.previousMonthCumulative.subscribers,
                    newSubscribers: item.monthlyInfo ? item.monthlyInfo.subscribers : 0,
                    totalSubscribers: item.currentMonthCumulative.subscribers,
                    newTransitionsCount: counters?.newTransitionsCount || 0,
                    newClaimsCount: counters?.newClaimsCount || 0,
                    newSubscribersCount: counters?.newSubscribersCount || 0,
                    initialNetRevenue: formatCurrency(item.previousMonthCumulative.revenue),
                    netRevenue: formatCurrency(item.monthlyInfo ? item.monthlyInfo.revenue : 0),
                    finalNetRevenue: formatCurrency(item.currentMonthCumulative.revenue),
                    crossPollinationEarnings: crossEarnings
                        ? formatCurrency(amountType === 'gross' ? crossEarnings.total : crossEarnings.totalNet)
                        : '',
                };
            });

            // Add totals row
            exportData.push({
                modelName: 'Total',
                initialSubscribers: '',
                newSubscribers: totals.newSubscribers,
                totalSubscribers: '',
                initialNetRevenue: '',
                netRevenue: formatCurrency(totals.netRevenue),
                finalNetRevenue: formatCurrency(totals.finalNetRevenue),
                crossPollinationEarnings: monthCrossEarnings
                    ? formatCurrency(
                          Array.from(monthCrossEarnings.values()).reduce(
                              (sum, earnings) => sum + (amountType === 'gross' ? earnings.total : earnings.totalNet),
                              0,
                          ),
                      )
                    : '',
            });

            const selectedMonthDisplay = moment(selectedMonth + '-01').format('MMM YYYY');
            const filename = `monthly_metrics_${selectedMonthDisplay.replace(' ', '_')}_${moment()
                .tz(timezone, true)
                .format('YYYY-MM-DD')}`;

            if (format === 'csv') {
                // Format JSON data to CSV
                const csv = parseCsv(exportData, {
                    fields: headers,
                    transforms: [],
                });

                // Convert CSV to Blob and download
                const blob: Blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${filename}.csv`);
                document.body.appendChild(link);
                link.click();
                link.parentNode && link.parentNode.removeChild(link);
            } else if (format === 'excel') {
                const workbook = XLSX.utils.book_new();
                const worksheet = XLSX.utils.json_to_sheet([]);
                XLSX.utils.sheet_add_aoa(worksheet, [headers.map(header => header.label)]);
                XLSX.utils.sheet_add_json(worksheet, exportData, {
                    origin: 'A2',
                    skipHeader: true,
                    header: headers.map(header => header.value),
                });
                XLSX.utils.book_append_sheet(workbook, worksheet, `${selectedMonthDisplay} Metrics`);
                XLSX.writeFile(workbook, `${filename}.xlsx`);
            }
        };

        const renderCrossPollinationEarnings = (group: OnlyFansMetricsGroupWithSummary) => {
            const isLoading = group.metrics.some(metric => crossPollinationLoadingMap.get(metric._id));
            const earnings = group.metrics.some(metric => crossPollinationEarningsMap.has(metric._id))
                ? group.metrics.reduce(
                      (acc, metric) => {
                          const metricEarnings = crossPollinationEarningsMap.get(metric._id);
                          if (metricEarnings) {
                              acc.total += d2f(metricEarnings.total);
                              acc.totalNet += d2f(metricEarnings.totalNet);
                          }
                          return acc;
                      },
                      { total: 0, totalNet: 0 },
                  )
                : null;
            if (isLoading) {
                return <Skeleton variant="text" width={'100%'} />;
            }

            if (!earnings) {
                return (
                    <Chip
                        label="N/A"
                        color="default"
                        sx={{
                            width: '100%',
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            justifyContent: 'flex-end',
                            color: theme.palette.getContrastText(theme.palette.grey[300]),
                            background: theme.palette.grey[300],
                        }}
                    />
                );
            }

            const amount = amountType === 'gross' ? earnings.total : earnings.totalNet;

            return (
                <Chip
                    label={`$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    color={'success'}
                    sx={{
                        width: '100%',
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        justifyContent: 'flex-end',
                        color: theme.palette.common.white,
                        background: theme.palette.success.main,
                        '& .MuiChip-root': {},
                    }}
                />
            );
        };

        return (
            <>
                <TableRow>
                    <TableCell>
                        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>
                    <TableCell>{group.name}</TableCell>
                    <TableCell align="center">{group.metrics.length.toLocaleString()}</TableCell>
                    <TableCell align="right">
                        <NumberChip value={group.subscribersTotal || 0} />
                    </TableCell>
                    <TableCell align="right">
                        <NumberChip value={group.claimsTotal || 0} />
                    </TableCell>
                    <TableCell align="right">
                        <NumberChip value={group.clicksTotal || 0} />
                    </TableCell>
                    <TableCell align="right">
                        <Chip
                            label={dinero({
                                amount: Math.trunc(
                                    group.earningsTotal && typeof group.earningsTotal !== 'number'
                                        ? d2f(group.earningsTotal) * (amountType === 'net' ? 0.8 : 1) * 100
                                        : 0,
                                ),
                                currency: 'USD',
                            }).toFormat()}
                            color={group.earningsTotal && typeof group.earningsTotal !== 'number' ? 'success' : 'default'}
                            sx={{
                                width: '100%',
                                fontFamily: 'monospace',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                justifyContent: 'flex-end',
                                color: theme.palette.common.white,
                                background:
                                    group.earningsTotal && typeof group.earningsTotal !== 'number'
                                        ? theme.palette.success.main
                                        : theme.palette.grey[300],
                                '& .MuiChip-root': {},
                            }}
                        />
                    </TableCell>
                    <TableCell align="right">{renderCrossPollinationEarnings(group)}</TableCell>
                    <TableCell align="center">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            <IconButton onClick={() => openGroupDialog(group._id!)} color="primary" size="small">
                                <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => openShareDialog(group._id!)} color="secondary" size="small">
                                <ShareIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteGroup(group._id!)} color="error" size="small">
                                <DeleteIcon />
                            </IconButton>
                        </Stack>
                    </TableCell>
                </TableRow>
                <TableRow
                    sx={{
                        bgcolor: 'grey.50',
                    }}
                >
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={open} timeout="auto" unmountOnExit={true}>
                            <Box
                                component={'div'}
                                sx={{
                                    padding: 1,
                                    // Follow the parent table's width instead of pushing the
                                    // cell (and with it the whole table) wider: width 0 keeps
                                    // this block out of the cell's intrinsic width, minWidth
                                    // stretches it back to the cell's final width.
                                    width: 0,
                                    minWidth: '100%',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <Typography variant="h6" gutterBottom component="div">
                                    Grouped Metrics
                                </Typography>
                                <Box component={'div'} sx={{ width: '100%', overflowX: 'auto' }}>
                                    <Table width="100%" size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={180}>Username</TableCell>
                                            <TableCell width={100}>Created At</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell width={90} align="center">
                                                Type
                                            </TableCell>
                                            <TableCell width={120} align="center">
                                                Last 24H
                                            </TableCell>
                                            <TableCell width={120} align="center">
                                                Claims/Subs
                                            </TableCell>
                                            <TableCell width={120} align="center">
                                                Clicks
                                            </TableCell>
                                            <TableCell width={100} align="center">
                                                CPF
                                            </TableCell>
                                            <TableCell width={150} align="center">
                                                Earnings {amountType === 'gross' ? 'Gross' : 'Net'}
                                            </TableCell>
                                            <TableCell align="center" width={150}>
                                                Cross-Poll. Earnings {amountType === 'gross' ? 'Gross' : 'Net'}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody
                                        sx={{
                                            // remove last bottom border
                                            '& tr:last-child td': {
                                                borderBottom: 'none',
                                            },
                                        }}
                                    >
                                        {group.metrics.map(metric => (
                                            <SextforceMetricsGroupsTableRowMetricRow
                                                key={metric._id}
                                                metric={metric}
                                                amountType={amountType}
                                                open={open}
                                                timezone={timezone}
                                                crossPollinationEarningsMap={crossPollinationEarningsMap}
                                                crossPollinationLoadingMap={crossPollinationLoadingMap}
                                            />
                                        ))}
                                        </TableBody>
                                    </Table>
                                </Box>

                                <AppBar
                                    position="static"
                                    sx={{
                                        mt: 2,
                                        width: '100%',
                                    }}
                                >
                                    <Tabs
                                        value={tabValue}
                                        onChange={handleTabChange}
                                        indicatorColor="secondary"
                                        textColor="inherit"
                                        variant="scrollable"
                                        scrollButtons="auto"
                                    >
                                        <Tab key={'total'} label={'Total'} value={'total'} />
                                        {availableMonths.map(month => (
                                            <Tab key={month.key} label={month.label} value={month.key} />
                                        ))}
                                    </Tabs>
                                </AppBar>

                                {groupSubscriberdAtVsTotalSalesDetailsLoading && (
                                    <Card sx={{ width: '100%', marginBottom: theme.spacing(2) }}>
                                        <CardContent>
                                            <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', p: 4 }}>
                                                <Typography variant="body2" sx={{ mb: 2 }}>
                                                    Loading subscriber data... ({fetchProgress.completed}/{fetchProgress.total})
                                                </Typography>
                                                <Box sx={{ position: 'relative', mb: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: 8,
                                                            backgroundColor: theme.palette.grey[200],
                                                            borderRadius: 4,
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                height: '100%',
                                                                backgroundColor: theme.palette.primary.main,
                                                                borderRadius: 4,
                                                                transition: 'width 0.3s ease-in-out',
                                                                width: `${progressPercentage}%`,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {Math.round(progressPercentage)}% complete
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}

                                {!groupSubscriberdAtVsTotalSalesDetailsLoading && tabValue === 'total' && (
                                    <>
                                        {monthlyCrossEarningsError && (
                                            <Typography variant="body2" color="error" sx={{ marginTop: theme.spacing(1) }}>
                                                Cross-pollination earnings: {monthlyCrossEarningsError}
                                            </Typography>
                                        )}
                                        <SextforceMetricsSubscribedAtVsSalesSummary
                                            metricId={group._id!}
                                            promoCampaignSubscribedAtVsSales={groupSubscriberdAtVsTotalSalesDetails}
                                            promoCampaignSubscribedAtVsSalesLoading={groupSubscriberdAtVsTotalSalesDetailsLoading}
                                            countersLastOfEveryMonth={groupCountersLastOfEveryMonth?.summary}
                                            countersLastOfEveryMonthLoading={groupCountersLastOfEveryMonthLoading}
                                            showEarningsAsGross={amountType === 'gross'}
                                            showEarningsWithSubscriptions={true}
                                            timezone={timezone}
                                            crossPollinationEarningsByMonth={crossPollinationEarningsByMonth}
                                            crossPollinationEarningsLoading={monthlyCrossEarningsFetching}
                                            onCalculateCrossPollinationEarnings={handleCalculateMonthlyCrossEarnings}
                                            calculateCrossPollinationEarningsDisabled={
                                                !crossPollinationAccountSelected || monthlyCrossEarningsFetching
                                            }
                                            calculateCrossPollinationEarningsTooltip={
                                                !crossPollinationAccountSelected
                                                    ? 'Select a cross-pollination account above to enable this calculation'
                                                    : "Calculate every month's cross-pollination earnings, one month at a time"
                                            }
                                        />
                                    </>
                                )}
                                {!groupSubscriberdAtVsTotalSalesDetailsLoading &&
                                    tabValue !== 'total' &&
                                    availableMonths.find(m => m.key === tabValue) && (
                                        <Card sx={{ width: '100%', marginBottom: theme.spacing(2) }}>
                                            <CardContent>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ marginBottom: theme.spacing(2) }}
                                                >
                                                    <Typography variant="h5" gutterBottom component="div">
                                                        {availableMonths.find(m => m.key === tabValue)?.label}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <MuiTooltip
                                                            title={
                                                                crossPollinationEarningsSubscriberId === 'none' ||
                                                                crossPollinationEarningsSubscriberId === ''
                                                                    ? 'Select a cross-pollination account above to enable this calculation'
                                                                    : "Calculate this month's cross-pollination earnings"
                                                            }
                                                        >
                                                            <span>
                                                                <IconButton
                                                                    disabled={
                                                                        crossPollinationEarningsSubscriberId === 'none' ||
                                                                        crossPollinationEarningsSubscriberId === '' ||
                                                                        monthlyCrossEarningsLoadingKey !== null
                                                                    }
                                                                    onClick={() => fetchMonthlyCrossEarnings(tabValue)}
                                                                    aria-label="Calculate cross-pollination earnings"
                                                                >
                                                                    <CalculateIcon
                                                                        fontSize="large"
                                                                        color={
                                                                            crossPollinationEarningsSubscriberId === 'none' ||
                                                                            crossPollinationEarningsSubscriberId === '' ||
                                                                            monthlyCrossEarningsLoadingKey !== null
                                                                                ? 'disabled'
                                                                                : 'secondary'
                                                                        }
                                                                    />
                                                                </IconButton>
                                                            </span>
                                                        </MuiTooltip>
                                                        <MuiTooltip title="Download as CSV">
                                                            <span>
                                                                <IconButton
                                                                    disabled={availableMonths.find(m => m.key === tabValue) ? false : true}
                                                                    onClick={() => exportMonthlyData('csv', tabValue)}
                                                                    aria-label="Download as CSV"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faFileCsv}
                                                                        size="2x"
                                                                        color={
                                                                            availableMonths.find(m => m.key === tabValue)
                                                                                ? theme.palette.info.main
                                                                                : theme.palette.text.disabled
                                                                        }
                                                                    />
                                                                </IconButton>
                                                            </span>
                                                        </MuiTooltip>
                                                        <MuiTooltip title="Download as Excel">
                                                            <span>
                                                                <IconButton
                                                                    disabled={availableMonths.find(m => m.key === tabValue) ? false : true}
                                                                    onClick={() => exportMonthlyData('excel', tabValue)}
                                                                    aria-label="Download as Excel"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faFileExcel}
                                                                        size="2x"
                                                                        color={
                                                                            availableMonths.find(m => m.key === tabValue)
                                                                                ? theme.palette.info.main
                                                                                : theme.palette.text.disabled
                                                                        }
                                                                    />
                                                                </IconButton>
                                                            </span>
                                                        </MuiTooltip>
                                                    </Stack>
                                                </Stack>
                                                {monthlyCrossEarningsError && (
                                                    <Typography variant="body2" color="error" sx={{ marginBottom: theme.spacing(1) }}>
                                                        Cross-pollination earnings: {monthlyCrossEarningsError}
                                                    </Typography>
                                                )}
                                                <TableContainer component={Card}>
                                                    <Table stickyHeader size="small" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                                        <TableHead>
                                                            <TableRow
                                                                sx={{
                                                                    '& th': {
                                                                        fontWeight: 'bold',
                                                                        textWrap: 'pretty',
                                                                        backgroundColor: theme.palette.grey[200],
                                                                    },
                                                                }}
                                                            >
                                                                <TableCell>Model</TableCell>
                                                                <TableCell align="right">Initial Subscribers (Found)</TableCell>
                                                                <TableCell align="right">New Subscribers (Found)</TableCell>
                                                                <TableCell align="right">Total Subscribers (Found)</TableCell>
                                                                <TableCell align="right">New Clicks (OF Counters)</TableCell>
                                                                <TableCell align="right">Clicks at Month End (OF Counters)</TableCell>
                                                                <TableCell align="right">New Claims (OF Counters)</TableCell>
                                                                <TableCell align="right">Claims at Month End (OF Counters)</TableCell>
                                                                <TableCell align="right">New Subscribers (OF Counters)</TableCell>
                                                                <TableCell align="right">Subscribers at Month End (OF Counters)</TableCell>
                                                                <TableCell align="right">Initial Net Revenue</TableCell>
                                                                <TableCell align="right">Net Revenue</TableCell>
                                                                <TableCell align="right">Final Net Revenue</TableCell>
                                                                <TableCell align="right">
                                                                    <MuiTooltip title="Deduplicated earnings the fans first attributed to this link during this month spent on the selected cross-pollination account(s). Rows never double-count a fan, so they sum cleanly and reconcile with the group-level cross-earnings.">
                                                                        <span>
                                                                            Cross-Poll. Earnings{' '}
                                                                            {amountType === 'gross' ? 'Gross' : 'Net'}
                                                                        </span>
                                                                    </MuiTooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <MonthlySubscriberTable
                                                            metrics={group.metrics}
                                                            selectedMonth={tabValue}
                                                            amountType={amountType}
                                                            timezone={timezone}
                                                            theme={theme}
                                                            isLoading={loadingSubscriberData}
                                                            subscriberDataMap={subscriberDataMap}
                                                        />
                                                    </Table>
                                                </TableContainer>
                                            </CardContent>
                                        </Card>
                                    )}
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </>
        );
    },
);

export default SextforceMetricsGroupsTableRow;
